import { UserQuery } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import TrackingEvent from 'constants/trackingEvents';
import { TIMEZONE_FORMAT } from 'helpers/dateHelpers/index';
import { useCreateConversation } from 'hooks/useCreateConversation';
import { useWindowSize } from 'hooks/useWindowSize';
import MenuTab from 'koba/components/MobileMenu/components/MenuTab';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { COURSES_ON_PROFILE_VIEW, SCREEN_LG } from 'utils/constants';
import { genericToastAlert } from 'utils/toastHelpers';
import { hasModeratorBadge } from 'utils/userHelpers';

import React, { useState } from 'react';

import { useQuery } from '@apollo/client';

import { Badge } from '@thinkific/chalk/Badge';
import { Button } from '@thinkific/chalk/Button';
import { Text } from '@thinkific/chalk/Text';
import { Prohibit, X } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import { useSiteFeaturesContext } from 'components/SiteFeaturesContext/SiteFeaturesContext';
import UserAvatar from 'components/UserAvatar';
import { useBlockedUsers } from 'components/new/chat/providers/ChatProvider/useBlockedUsers';

import { USER_QUERY } from 'schema/User/queries';

import * as css from './ProfilePanel.css';
import styles from './ProfilePanel.module.scss';
import ProfileCompletedCourses from './components/ProfileCompletedCourses/ProfileCompletedCourses';
import ProfilePanelSkeleton from './components/ProfilePanelSkeleton/ProfilePanelSkeleton';
import ProfilePastActivity from './components/ProfilePastActivity/ProfilePastActivity';

const cx = classNames.bind(styles);
interface ProfilePanelProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({ isOpen, onClose, userId }) => {
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const { t } = useTranslation();
  const { trackEvent, currentUser, community } = useCommunityContext();
  const { isDMsAccessible } = useSiteFeaturesContext();
  const { width } = useWindowSize();
  const isMobile = width < SCREEN_LG;

  const { isUserBlocked } = useBlockedUsers();
  const { createConversationWithUser, navigateToConversation, loadingConversationUserId } =
    useCreateConversation();

  const createAndNavigateToConversation = async () => {
    const conversationId = await createConversationWithUser(user?.gid || '');
    if (conversationId) {
      onClose();
      navigateToConversation(conversationId);
    }
  };

  const handleViewHistoryClick = async () => {
    trackEvent(TrackingEvent.DIRECT_MESSAGES_VIEW_HISTORY_CLICKED);
    await createAndNavigateToConversation();
  };

  const handleMessageClick = async () => {
    trackEvent(TrackingEvent.DIRECT_MESSAGES_MESSAGE_BUTTON_CLICKED);
    await createAndNavigateToConversation();
  };

  const {
    data,
    loading: isUserQueryLoading,
    fetchMore,
    previousData,
  } = useQuery<UserQuery>(USER_QUERY, {
    variables: {
      communityId: community.id,
      userId,
      numOfCourses: COURSES_ON_PROFILE_VIEW,
    },
    fetchPolicy: 'cache-and-network',
    skip: !userId,
    onError: () => {
      genericToastAlert();
    },
  });

  const user = data?.community?.communityUser?.user;
  const hasRoles = user?.roles?.length;
  const canModerate = data?.community?.communityUser?.canModerate;
  const userHasModeratorBadge = hasRoles ? hasModeratorBadge(user) : false;
  const isModerator = userHasModeratorBadge || canModerate;
  const isBlocked = isUserBlocked(user?.gid || '');

  const roles = userHasModeratorBadge ? t('Moderator') : user?.roles?.join(', ');

  const isDifferentUser = user?.id !== previousData?.community?.communityUser?.user?.id;

  const handleEditProfile = (event) => {
    event.preventDefault();
    trackEvent(TrackingEvent.EDIT_PROFILE_FROM_PROFILE_PANEL_CLICKED, {});
    window.location.href = '/account';
  };

  const formatUserLocalTime = () => {
    const localTime = DateTime.now()
      .setZone(user?.profile?.timezone || '')
      .toFormat(TIMEZONE_FORMAT, {
        locale: currentUser.locale,
      });

    return localTime;
  };

  const onLoadMoreCourses = async () => {
    setIsLoadingMore(true);
    await fetchMore({
      variables: {
        endCursor: user?.enrollments?.pageInfo?.endCursor,
      },
    });
    setIsLoadingMore(false);
  };

  const renderSubtitle = () => {
    if (!user?.profile) return null;

    const { headline, company } = user?.profile;

    if (!headline && !company) return null;

    let subtitle = '';

    if (headline) {
      subtitle += headline;
    }

    if (company) {
      if (subtitle.trim()) {
        subtitle += `, ${company}`;
      } else {
        subtitle += company;
      }
    }

    return (
      <Text as="p" title={subtitle} variant="body.md">
        {subtitle}
      </Text>
    );
  };

  const closeButton = (
    <Button
      data-qa="profile-panel__close"
      emphasis="low"
      icon={X}
      size="sm"
      hiddenLabel
      onClick={onClose}
    >
      {t('Close profile panel')}
    </Button>
  );

  const hasNoProfile = !isUserQueryLoading && userId && !data?.community?.communityUser;

  return (
    <MenuTab
      hasHeader={false}
      isOpen={isOpen}
      menuTitle={t('Profile')}
      size={isMobile ? 'medium' : 'large'}
      slideTo="left"
      hasOverlay
      onClose={onClose}
    >
      <div
        aria-label={`${user?.fullName} profile.`}
        className={cx('profile-panel')}
        data-qa="profile-panel__wrapper"
        role="dialog"
      >
        <div className={css.profileHeader}>
          <Text as="h3" variant="display.sm">
            {t('Profile')}
          </Text>
          {closeButton}
        </div>
        <div className={css.profileWrapper}>
          {isUserQueryLoading && isDifferentUser ? (
            <div className={cx('profile-panel__skeleton')}>
              <ProfilePanelSkeleton />
              {closeButton}
            </div>
          ) : (
            <div className={cx('profile-panel__content')}>
              <div className={css.profileDetails}>
                <UserAvatar
                  avatarUrl={user?.profile?.avatarUrl || ''}
                  className={cx('profile-panel__avatar')}
                  size="lg"
                  userId={user?.id || ''}
                  username={user?.fullName || ''}
                />

                <div className={css.profileName}>
                  {user?.fullName && (
                    <Text as="h2" title={user?.fullName} variant="display.sm">
                      {user?.fullName}
                    </Text>
                  )}
                  {isModerator && (
                    <Badge
                      emphasis="medium"
                      label={t('Moderator', 'Moderator')}
                      variant="primary"
                    />
                  )}
                </div>

                {user?.profile && renderSubtitle()}
                {user?.profile?.timezone && (
                  <Text as="p" variant="body.md">
                    {t('Local time')} <span>{formatUserLocalTime()}</span>
                  </Text>
                )}

                {isBlocked && (
                  <div className={css.dmsRow}>
                    <Badge
                      emphasis="medium"
                      icon={Prohibit}
                      label={t('components-chat-block_dms_blocked', 'DMs blocked')}
                      roundness="full"
                      variant="destructive"
                    />
                    <Button
                      emphasis="low"
                      isLoading={loadingConversationUserId === user?.gid}
                      size="xs"
                      onClick={handleViewHistoryClick}
                    >
                      {t('components-chat-view_chat_history', 'View chat history')}
                    </Button>
                  </div>
                )}
                {isDMsAccessible && !isBlocked && currentUser.id !== userId && (
                  <div className={css.dmsRow}>
                    <Button
                      emphasis="medium"
                      isLoading={loadingConversationUserId === user?.gid}
                      size="xs"
                      onClick={handleMessageClick}
                    >
                      {t('components-chat-message_user', 'Message')}
                    </Button>
                  </div>
                )}
                {currentUser.id === userId && (
                  <a
                    className={cx('profile-panel__link')}
                    href="/account"
                    onClick={handleEditProfile}
                  >
                    {t('Edit Profile')}
                  </a>
                )}
              </div>

              {!roles?.length && (
                <ProfileCompletedCourses
                  enrollments={user?.enrollments}
                  isLoadingMore={isLoadingMore}
                  onLoadMoreCourses={onLoadMoreCourses}
                />
              )}
            </div>
          )}

          {hasNoProfile ? (
            <section className={cx('profile-panel__no-info')}>
              <Text as="h2" data-qa="profile-panel__no-info__title" variant="display.sm">
                {t('components-profile-no_profile_information', 'No profile information.')}
              </Text>
              <Text as="p" variant="body.md">
                {t(
                  'components-profile-no_info_text',
                  'This person may no longer be part of the community.'
                )}
              </Text>
            </section>
          ) : (
            <ProfilePastActivity user={user} userId={userId} onClose={onClose} />
          )}
        </div>
      </div>
    </MenuTab>
  );
};

export default ProfilePanel;
